import React, { useEffect } from "react";
import { Modal, Button, Spin, Typography } from "antd";
import { Editor } from "@monaco-editor/react";
import { useTranslation } from "react-i18next";
import { useWorkerCode } from "../hooks/useAppsQueries";
import { useTheme } from "../contexts/ThemeContext";

export interface WorkerCodeModalProps {
  /** id апки, для якої показувати код; null = модалка закрита */
  appId: string | null;
  onClose: () => void;
  /** Тема Monaco (vs-dark / light); за замовчуванням з useTheme() */
  monacoTheme?: string;
  /** Викликається при зміні стану завантаження (для індикатора на кнопці в таблиці) */
  onLoadingChange?: (loading: boolean) => void;
}

export const WorkerCodeModal: React.FC<WorkerCodeModalProps> = ({
  appId,
  onClose,
  monacoTheme: monacoThemeProp,
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const { monacoTheme: themeFromContext } = useTheme();
  const monacoTheme = monacoThemeProp ?? themeFromContext;

  const workerCodeQuery = useWorkerCode(appId);

  useEffect(() => {
    onLoadingChange?.(workerCodeQuery.isFetching);
  }, [workerCodeQuery.isFetching, onLoadingChange]);

  const open = appId != null;

  return (
    <Modal
      title={t("apps.workerCodeTitle")}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t("apps.close")}
        </Button>,
      ]}
      width={900}
    >
      {workerCodeQuery.isFetching ? (
        <Spin size="large" tip={t("apps.loading")} />
      ) : workerCodeQuery.isError ? (
        <Typography.Text type="danger">
          {t("apps.workerCodeError")}
        </Typography.Text>
      ) : (
        <Editor
          height="500px"
          language="javascript"
          theme={monacoTheme}
          value={workerCodeQuery.data?.code ?? ""}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 12,
            automaticLayout: true,
            renderLineHighlight: "none",
          }}
        />
      )}
    </Modal>
  );
};
